import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Render outside the scrolling answer area so menus never resize or get clipped by it.
export default function PickerPopover({
  anchorRef,
  onClose,
  children,
  focusFirst = false,
  dir,
  ...props
}) {
  const popupRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const [position, setPosition] = useState(null);
  const focused = useRef(false);
  useLayoutEffect(() => {
    if (position && focusFirst && !focused.current) {
      focused.current = true;
      popupRef.current?.querySelector('input')?.focus();
    }
  }, [position, focusFirst]);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return undefined;
    const positionMenu = () => {
      const rect = anchor.getBoundingClientRect();
      const viewport = window.visualViewport;
      const top = viewport?.offsetTop || 0;
      const left = viewport?.offsetLeft || 0;
      const height = viewport?.height || window.innerHeight;
      const width = viewport?.width || window.innerWidth;
      const below = top + height - rect.bottom - 12;
      const above = rect.top - top - 12;
      const opensAbove = below < 260 && above > below;
      const menuWidth = Math.min(Math.max(rect.width, 240), width - 16);
      const menuLeft = dir === 'rtl' ? rect.right - menuWidth : rect.left;
      setPosition({
        width: menuWidth,
        left: Math.max(
          left + 8,
          Math.min(menuLeft, left + width - menuWidth - 8),
        ),
        top: opensAbove ? undefined : rect.bottom + 6,
        bottom: opensAbove ? window.innerHeight - rect.top + 6 : undefined,
        maxHeight: Math.max(44, Math.min(260, opensAbove ? above : below)),
      });
    };
    const dismiss = (event) => {
      if (
        !anchor.contains(event.target) &&
        !popupRef.current?.contains(event.target)
      ) {
        closeRef.current();
      }
    };
    const escape = (event) => {
      if (event.key !== 'Escape') return;
      if (popupRef.current?.contains(document.activeElement)) anchor.focus();
      closeRef.current();
    };
    positionMenu();
    window.addEventListener('resize', positionMenu);
    window.addEventListener('scroll', positionMenu, true);
    window.visualViewport?.addEventListener('resize', positionMenu);
    window.visualViewport?.addEventListener('scroll', positionMenu);
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('focusin', dismiss);
    document.addEventListener('keydown', escape);
    return () => {
      window.removeEventListener('resize', positionMenu);
      window.removeEventListener('scroll', positionMenu, true);
      window.visualViewport?.removeEventListener('resize', positionMenu);
      window.visualViewport?.removeEventListener('scroll', positionMenu);
      document.removeEventListener('pointerdown', dismiss);
      document.removeEventListener('focusin', dismiss);
      document.removeEventListener('keydown', escape);
    };
  }, [anchorRef, dir]);

  return createPortal(
    <div
      {...props}
      dir={dir}
      ref={popupRef}
      className={`scout-setup__popover ${props.className || ''}`}
      style={{ ...position, visibility: position ? 'visible' : 'hidden' }}
    >
      {children}
    </div>,
    document.body,
  );
}
